import { FileNotFoundError, IllegalArgumentError, IllegalStateError } from "@core/errors/errors-core";
import { generateDeterministicId, generateDeterministicTemplateIdFromTemplateIdAndTemplateId, generateId, makeUUID } from '@core/services/orm/idGenerator';
import { BaseEntity, EntityContext } from "@core/services/orm/orm";
import type { PropertyDescription, ValuePropertyDescription } from "@archipad-js/core/orm";

import { getProjectInfo, newPatchId, prepareAttachment } from "@archipad/backend/archiweb/synchroDriver";
import { makeTempBugConcrete } from "@archipad/backend/project/bug";
import WorkflowManager from '@archipad/backend/project/workflowManager';
import { DrawingHint } from "@archipad/helpers/exif";
import { Account_Service_DIID } from "@archipad/services/account.service";
import { getArchipadAbilityService } from "@archipad/services/archipad-ability.module";
import { AsyncUploadAttachmentService_DIID } from "@archipad/services/async-upload-attachment.service";
import { BugFacade } from "@archipad/services/bug.facade";
import { PatchDescriptor, PatchesDescriptor } from "@archipad/services/file/patchFile";
import OfflineServiceInstance from "@archipad/services/offline.service";
import { AnnotationService, BugService, PhaseService } from "@archipad-js/archipad/entity-services";
import { CacheFileDescriptor } from "@core/drivers/cacheFile/browser/cacheFileDescriptor";
import { Data } from "@core/services/file/file";


import { ProjectManager } from "@ui-archipad/backend/project/project";
import { sharedProjectList } from "@ui-archipad/backend/project/projectList";
import { voidAbortSignal, voidProgress } from "@core/tasks/asyncCaller";
import { isNotNullish } from "@archipad-js/core/utils";
import pLimit from 'p-limit';
import { clearAllCaches, ClearAllCachesResult } from "@ui-archipad/helpers/clearAllCaches";
import { FileService } from "@core/services/file/file";
import { firstValueFrom } from 'rxjs';
import _LegacyServiceManager from "@core/services/legacy-service-manager";
import { Archiweb_Services_Client_DIID } from "@archipad-js/archipad/archiweb-services-client";


import BugNoteFacadeInstance from '@archipad/services/bugNote.facade';
import { SignOffService } from "@archipad/services/signOff.service";
import { WorkflowService } from "@archipad-js/archipad/entity-services";
import { EquivBugState, EquivBugTransition, BugEntity, BugGroupEntity, BugNoteEntity, BugStateEntity, MAP_FILE_TYPES, MAP_TYPES, MapEntity, PhaseEntity, ProjectEntity } from "@archipad-js/archipad-model";

/**
 * An integrity issue found while scanning entities with {@link Commands.checkAllEntities}.
 */
interface EntityIntegrityIssue {
    entityType: string;
    entityId: string;
    property: string;
    /** `missing`: a mandatory value/relation is null/undefined. `type`: the value type does not match the schema. */
    kind: 'missing' | 'type';
    message: string;
}


/**
 * What {@link Commands.autoFixProject} did, by step.
 */
export interface AutoFixProjectResult {
    /** The changes made in the project */
    fixed: Record<string, number>;
    /** The issues found and not fixed, to fix by hand */
    remaining: Record<string, number>;
}

/**
 * To use the follow commands you have to open a project on [Archipad Web](https://cloud.archipad.com).
 * Open the developer console. All commands are available under `Archipad.Commands`.
 *
 * @private
 */
export class Commands {
    static async getAllUserProjects(): Promise<ReadonlyArray<string>> {
        const projectList = sharedProjectList();

        return projectList.getProjectsIds();
    }

    /**
     * @internal
     * Retrieve the current entity context.
     */
    static async getEntityContext(): Promise<EntityContext> {
        const archipadApp = document.querySelector('archipad-app') as any;
        const projectManager = archipadApp?.project as ProjectManager | null | undefined;
        if (!projectManager) {
            throw new IllegalStateError('Open a project first: the command applies to the open project');
        }
        const projectContext = await projectManager.getContext(null).toPromise();
        return projectContext.getEntityContext();
    }

    /**
     * @internal
     * Will delete the Map entity of a given id.
     *
     * @param mapId - The id of the Map entity to delete
     */
    static async deleteMap(mapId: string): Promise<void> {
        if (typeof mapId !== 'string') {
            throw new IllegalArgumentError(`The mapId must be a string`);
        }

        const entityContext = await Commands.getEntityContext();

        const map = entityContext.getEntity('Map', mapId);

        if (map === null) {
            throw new IllegalArgumentError(`Unable to find the Map<${mapId}>`);
        }

        entityContext.deleteEntity(map);

        console.log(`The Map ${mapId} has been deleted`);
    }

    /**
     * Will delete all empty FormObs maps.
     *
     * Example:
     * ```
     * > Archipad.Commands.deleteAllEmptyFormObsMaps()
     * The following map contains 1 observations and will not be deleted.
     * entityType {_entityContext: EntityContext, _attachments: {…}, _values: {…}, _updating: false}
     * Promise {<fulfilled>: "3 maps have been deleted"}
     * ```
     * @return `"<<number of deleted map>> maps have been deleted"`
     */
    static async deleteAllEmptyFormObsMaps(): Promise<string> {
        const entityContext = await Commands.getEntityContext();
        const allMaps = Object.values(entityContext.entitiesByType['Map']) as MapEntity[];
        const emptyFormObsMaps = allMaps
            .filter((map) => map.fileType === MAP_FILE_TYPES.PDF && map.type === MAP_TYPES.FORMS)
            .filter((map) => {
                if (map.bugs.length === 0) {
                    return true;
                }

                console.warn(`The following map contains ${map.bugs.length} observations and will not be deleted.`, map);
                return false;
            });

        let deleteCount = 0;

        for (const map of emptyFormObsMaps) {
            if (map.bugs.length === 0) {
                entityContext.deleteEntity(map);
                deleteCount++;
            }
        }

        return `${deleteCount} maps have been deleted`;
    }

    /**
     * @internal
     * Will output in the browser console the current project AWS S3 prefix.
     */
    static async getProjectPrefix(): Promise<void> {
        const archipadApp = document.querySelector('archipad-app') as any;
        const projectManager = archipadApp.project as ProjectManager | null;

        if (projectManager === null) {
            throw new IllegalStateError('Cannot retrieve the project prefix outside of a project');
        }

        const projectInfo = await getProjectInfo(null, projectManager.getId()).toPromise();
        const projectPrefix = `${projectInfo.patches.bucket}/${projectInfo.patches.path}`;

        console.log(`Project prefix: %c${projectPrefix}`, 'color: blue;');
        console.table({
            'Region': projectInfo.patches.region,
            'Bucket': projectInfo.patches.bucket,
            'Path': projectInfo.patches.path,
        });
    }

    /**
     * Create a Phase_BugGroups entity to associate a Phase to a BugGroup.
     *
     * @param bugGroup - The BugGroup to associate to the Phase
     * @param phase - The Phase to associate to the BugGroup
     * @internal
     */
     static async createPhaseRelation(bugGroup: BugGroupEntity | null, phase: PhaseEntity): Promise<void> {
        const project = await Commands.getProject();
        const entityContext = project.getContext();
        const relationTemplateId = generateDeterministicTemplateIdFromTemplateIdAndTemplateId(phase.templateId, bugGroup?.templateId ?? '6485251F3220412089A38D3E28701777');
        const relationEntityId = generateDeterministicId(project.projectId, relationTemplateId, 'uuid');
        const relation = entityContext.getEntity('Phase_BugGroups', relationEntityId) ?? entityContext.createEntity('Phase_BugGroups', relationEntityId);
        relation.templateId = relationTemplateId;
        relation.phase = phase;
        relation.bugGroup = bugGroup;
    }


    /**
     * Scan every entity of the current project's entity context and verify its data integrity.
     *
     * For each entity, every property described in its entity type metadata is checked:
     * - mandatory (non-optional) value properties and single relations must not be `null`/`undefined`
     * - present value properties must match their expected type (`string`, `number`, `boolean`, `date`, `object`)
     *
     * Calculated properties and to-many relations are skipped.
     *
     * Example:
     * ```
     * > Archipad.Commands.checkAllEntities()
     * ❌ Bug<...> "openDate" is mandatory but is null
     * ❌ Map<...> "displayScale" expected number but got string ("1.5")
     * Promise {<fulfilled>: {scannedEntities: 1234, issueCount: 2, ...}}
     * ```
     *
     * @returns A summary of the scan with the issues found grouped by entity type.
     * @internal
     */
    static async checkAllEntities(): Promise<{
        scannedEntities: number;
        issueCount: number;
        issuesByEntityType: Record<string, EntityIntegrityIssue[]>;
    }> {
        const entityContext = await Commands.getEntityContext();

        const issues: EntityIntegrityIssue[] = [];
        let scannedEntities = 0;

        for (const [entityTypeName, entitiesById] of Object.entries(entityContext.entitiesByType)) {
            for (const entity of Object.values(entitiesById)) {
                scannedEntities++;
                const metadata = entity.entityType.metadata;

                for (const propertyName in metadata) {
                    const property: PropertyDescription = metadata[propertyName];

                    // calculated getters are derived from other data, nothing to validate
                    if (property.calculated) {
                        continue;
                    }

                    const value = (entity as unknown as Record<string, unknown>)[propertyName];
                    const isNullish = value === null || value === undefined;

                    if (property.relation) {
                        // to-many relations are always arrays, they cannot be nullish
                        if (!property.single) {
                            continue;
                        }
                        if (!property.optional && isNullish) {
                            issues.push({
                                entityType: entityTypeName,
                                entityId: entity.id,
                                property: propertyName,
                                kind: 'missing',
                                message: `mandatory relation is ${value === undefined ? 'undefined' : 'null'}`,
                            });
                        }
                        continue;
                    }

                    // value property
                    if (isNullish) {
                        if (!property.optional) {
                            issues.push({
                                entityType: entityTypeName,
                                entityId: entity.id,
                                property: propertyName,
                                kind: 'missing',
                                message: `mandatory value is ${value === undefined ? 'undefined' : 'null'}`,
                            });
                        }
                        continue;
                    }

                    const valueProperty = property as ValuePropertyDescription;
                    if (!Commands.isValidPropertyValueType(valueProperty.type, value)) {
                        issues.push({
                            entityType: entityTypeName,
                            entityId: entity.id,
                            property: propertyName,
                            kind: 'type',
                            message: `expected ${valueProperty.type} but got ${typeof value} (${JSON.stringify(value)})`,
                        });
                    }
                }
            }
        }

        const issuesByEntityType: Record<string, EntityIntegrityIssue[]> = {};
        for (const issue of issues) {
            (issuesByEntityType[issue.entityType] ??= []).push(issue);
        }

        if (issues.length === 0) {
            console.log(`✅ Scanned ${scannedEntities} entities, no integrity issue found.`);
        } else {
            console.error(`❌ Scanned ${scannedEntities} entities, found ${issues.length} integrity issue(s):`);
            for (const issue of issues) {
                const label = issue.kind === 'missing' ? 'mandatory' : 'type';
                console.error(
                    `❌ [${label}] ${issue.entityType}<${issue.entityId}> "${issue.property}": ${issue.message}`,
                );
            }
        }

        return {
            scannedEntities,
            issueCount: issues.length,
            issuesByEntityType,
        };
    }

    /**
     * @internal
     * Check that a value matches the expected ORM value property type.
     */
    private static isValidPropertyValueType(type: string, value: unknown): boolean {
        switch (type) {
            case 'string':
                return typeof value === 'string';
            case 'number':
                return typeof value === 'number' && !Number.isNaN(value);
            case 'boolean':
                return typeof value === 'boolean';
            case 'date':
                return value instanceof Date && !Number.isNaN(value.getTime());
            case 'object':
                return typeof value === 'object';
            default:
                return true;
        }
    }

    /**
     * Set a list of templates which the project must depend on.
     * The content of each template will be added to the project.
     *
     * To apply changes to the project we must reload the page.
     *
     * Example:
     * ```
     * > Archipad.Commands.updateOriginTemplates(['831DA8A6A0F9AA28E3F9C0CAD530ABAD'])
     * Promise {<fulfilled>: undefined}
     * ```
     * @param {string[]} originTemplateIds List of templateId to apply to the project
     */
    static async updateOriginTemplates(originTemplateIds: string[]): Promise<void> {
        const re = /^[0-9A-F]{32}$/i;
        const isValid = originTemplateIds.every((templateId) => {
            if (!re.test(templateId)) {
                console.error(`Invalid template id: ${templateId}`);
                return false;
            }
            return true;
        })
        if (!isValid) {
            return;
        }

        const project = await Commands.getProject();
        project.originTemplateIds = originTemplateIds.join(',');

        const archipadApp = document.querySelector('archipad-app');
        const projectManager = (archipadApp as unknown as any).project as ProjectManager | null;
        const projectContext = await projectManager.getContext(null).toPromise();
        await projectManager.upgradeProjectUsingContext(null, projectContext).toPromise();
    }

    /**
     * Utility to make a new patch id.
     *
     * @internal
     */
    static async makeNewPatchId(): Promise<{patchId: string}> {
        const patchId = await newPatchId(voidAbortSignal(), voidProgress());

        return { patchId };
    }

    /**
     * Utility to make a new template id.
     *
     * @internal
     */
    static async makeNewTemplateId(): Promise<{templateId: string}> {
        const templateId = makeUUID();

        return { templateId };
    }

        /**
     * @internal
     * Return the current project entity
     */
    static async getProject(): Promise<ProjectEntity> {
        const entityContext = await Commands.getEntityContext();
        const projects = Object.values(entityContext.entitiesByType.Project) as ProjectEntity[];
        return projects[0];
    }

    /**
     * @internal
     */
    static decimalStringToUuid(decimalString: string): { uuid: string } {
        const hex = BigInt(decimalString).toString(16).padStart(32, "0");
	    const uuid = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
        return { uuid };
    }

    /**
     * @internal
     */
    static uuidToDecimalString(uuid: string): { decimalString: string } {
        const hex = uuid.replace(/-/g, "");
	    const decimalString = BigInt(`0x${hex}`).toString();
        return { decimalString };
    }
static async createProjectTest(): Promise<unknown> {
        const archiwebServicesClient = _LegacyServiceManager.container.get(Archiweb_Services_Client_DIID.symbol);
        const newProject: {
            id: string;
        } = await archiwebServicesClient.makeAuthenticatedRequest(
            voidAbortSignal(),
            "project",
            "create",
            {
                id: generateId(),
                name: "Test project from console",
            },
        ) as any;
        console.error('New project creation initiated. Fetching project info...', newProject);
        const getProjectInfo = await archiwebServicesClient.makeAuthenticatedRequest(
            voidAbortSignal(),
            "project",
            "get",
            {
                id: newProject['id'],
            },
        );
        console.error('Project info fetched:', getProjectInfo);

        const updateParams = {
            id: '6493672236199286866',
            name: 'Updated project name from console',
            patchId: '0000019c0f8e7a6b.8e578e53'
            // bugWorkflow = projectInfo.dependencies.indexOf('workflow') !== 1;
            // dependencies = projectInfo.dependencies;
        };
        await archiwebServicesClient.makeAuthenticatedRequest(voidAbortSignal(), 'project', 'update', updateParams);


        return getProjectInfo;
}

    /**
     * Deletes everything the application keeps in this browser, then reloads it: the service workers, the databases
     * (projects, local changes not published yet), the caches, the local and session storages (the session).
     *
     * ```
     * > await Archipad.Commands.clearAllCaches()
     * ```
     *
     * The page is blocked for a minute at most, and the other windows of the application until the end: they close
     * their database, then reload.
     *
     * @param reload - Reloads the page after
     * @returns What is still there after a minute (a database still open in another window...)
     */
    static async clearAllCaches({ reload = true }: { reload?: boolean } = {}): Promise<ClearAllCachesResult> {
        const result = await clearAllCaches();
        if (reload) {
            window.location.reload();
        }
        return result;
    }

    /**
     * Reloads the application without its caches, to get its latest version: unregisters the service workers, deletes
     * the caches they keep the application in, and downloads again the files of the application the page loaded, which
     * replaces their copies in the HTTP cache of the browser (no API clears that cache). Unlike
     * {@link Commands.clearAllCaches}, it keeps the local database (projects, local changes), the session, and the
     * project files kept for offline (`aws-assets-cache`).
     *
     * Online only: offline, the application could not load again.
     *
     * ```
     * > await Archipad.Commands.hardRefresh()
     * ```
     *
     * @param reload - Reloads the page after
     * @returns What was deleted
     */
    static async hardRefresh({ reload = true }: { reload?: boolean } = {}): Promise<{ deletedCaches: string[]; unregisteredServiceWorkers: number; refreshedFiles: number }> {
        if (OfflineServiceInstance.isOffline()) {
            throw new IllegalStateError('Offline: the application could not load again without its caches');
        }
        const keptCaches = new Set(['aws-assets-cache']);
        const deletedCaches = 'caches' in window ? (await caches.keys()).filter((name) => !keptCaches.has(name)) : [];
        await Promise.all(deletedCaches.map((name) => caches.delete(name)));
        const registrations = navigator.serviceWorker ? await navigator.serviceWorker.getRegistrations() : [];
        await Promise.all(registrations.map((registration) => registration.unregister()));
        console.log(`✅ Deleted ${deletedCaches.length} cache(s) (${deletedCaches.join(', ') || 'none'}), unregistered ${registrations.length} service worker(s)`);

        const files = Commands.applicationFileUrls();
        const limit = pLimit(8);
        let refreshedFiles = 0;
        await Promise.all(files.map((url) => limit(async () => {
            try {
                const response = await fetch(url, { cache: 'reload', credentials: 'same-origin' });
                if (response.ok) {
                    refreshedFiles++;
                } else {
                    console.warn(`⚠️ ${url} could not be downloaded again: ${response.status}`);
                }
            } catch (error) {
                console.warn(`⚠️ ${url} could not be downloaded again`, error);
            }
        })));
        console.log(`✅ Downloaded again ${refreshedFiles} of the ${files.length} file(s) of the application, bypassing the HTTP cache`);

        if (reload) {
            window.location.reload();
        }
        return { deletedCaches, unregisteredServiceWorkers: registrations.length, refreshedFiles };
    }

    /**
     * @internal
     * The files of the application the page loaded (its own origin only): the page, its scripts and stylesheets, and
     * every resource the browser timed. The `ws-local/` files only exist in the cache of the service worker.
     */
    private static applicationFileUrls(): string[] {
        const urls = new Set<string>([location.href.split('#')[0]]);
        for (const entry of performance.getEntriesByType('resource')) {
            urls.add(entry.name);
        }
        for (const element of Array.from(document.querySelectorAll<HTMLScriptElement | HTMLLinkElement>('script[src], link[href]'))) {
            urls.add(element instanceof HTMLScriptElement ? element.src : element.href);
        }
        return Array.from(urls).filter((url) => {
            try {
                return new URL(url).origin === location.origin && !url.includes('ws-local/');
            } catch {
                return false;
            }
        });
    }

    /**
     * Checks that every attachment of the project is on the server (HEAD request), and logs their sizes.
     *
     * @returns The number of attachments that could not be fetched
     */
    static async checkAllAttachments(): Promise<{ failedAttachments: number }> {
        let failedAttachments = 0;
        async function fetchAttachmentByEntities(entities: BaseEntity[]): Promise<string[]>{
        const attachmentsToPrefetch = [
            entities.flatMap(annotation => Object.values(annotation?.getAttachments() ?? {})),
        ].flat().filter(isNotNullish);
        const concurrencyLimit = pLimit(100);
        const totalLoadedFiles = [];
        const fetchAttachments$ = attachmentsToPrefetch.map(async (attachment, index) =>
            concurrencyLimit(async () => {
                if (attachment.type === 'localCache'){
                    // local cache attachments are already cached, no need to fetch them
                    return;
                }
                try {
                    const head = await firstValueFrom(FileService.metadata(attachment));
                    totalLoadedFiles.push(head.contentLength);
                } catch (error) {
                    failedAttachments++;
                    console.error(`❌ Failed to prefetch attachment ${attachment} for project`, error);
                }
            }));
        await Promise.all(fetchAttachments$)
        return totalLoadedFiles;

        }

        const projectEntity = await Commands.getProject();
        const projectId = projectEntity.id;
        const maps = projectEntity.maps.map(map => map.activeVersion);
        const documents = projectEntity.documents.map(document => document.activeVersion);
        const bugNotesAnnotations = projectEntity.bugs.flatMap(bug => bug.notes).flatMap(note => [note, ...note.annotations]);
        const bugAnnotations = projectEntity.bugs.flatMap(bug => [bug, ...bug.annotations]);
        const remarksAnnotations = projectEntity.remarks.flatMap(remark => [remark, ...remark.annotations]);
        const reports = Array.from(projectEntity.reports);
        const attachments = {
            bugNotesAnnotations,
            bugAnnotations,
            remarksAnnotations,
            documents,
            maps,
            project: projectEntity,
            reports,
        }

        const bugsHead = await fetchAttachmentByEntities(attachments.bugAnnotations);
        const bugsTotal = bugsHead.map((size) => Commands.convertBytesToGo(parseInt(size))).reduce((acc, size) => acc + size, 0);
        console.log(`bugsHead complete`);
        const reportsHead = await fetchAttachmentByEntities(attachments.reports);
        const reportsTotal = reportsHead.map((size) => Commands.convertBytesToGo(parseInt(size))).reduce((acc, size) => acc + size, 0);
        console.log(`reportsHead complete`);
        const bugsNoteHead = await fetchAttachmentByEntities(attachments.bugNotesAnnotations);
        const bugsNoteTotal = bugsNoteHead.map((size) => Commands.convertBytesToGo(parseInt(size))).reduce((acc, size) => acc + size, 0);
        console.log(`bugsNoteHead complete`);
        const remarksHead = await fetchAttachmentByEntities(attachments.remarksAnnotations);
        const remarksTotal = remarksHead.map((size) => Commands.convertBytesToGo(parseInt(size))).reduce((acc, size) => acc + size, 0);
        console.log(`remarksHead complete`);
        const documentsHead = await fetchAttachmentByEntities(attachments.documents);
        const documentsTotal = documentsHead.map((size) => Commands.convertBytesToGo(parseInt(size))).reduce((acc, size) => acc + size, 0);
        console.log(`documentsHead complete`);
        const mapsHead = await fetchAttachmentByEntities(attachments.maps);
        const mapsTotal = mapsHead.map((size) => Commands.convertBytesToGo(parseInt(size))).reduce((acc, size) => acc + size, 0);
        console.log(`mapsHead complete`);
        const totalAttachmentsSize = bugsTotal + reportsTotal + bugsNoteTotal + remarksTotal + documentsTotal + mapsTotal;
        console.log(
            'All attachments have been processed',{
            projectId,
            totalAttachmentsSize: `${totalAttachmentsSize.toFixed(2)}`,
            totalLength: attachments.bugAnnotations.length + attachments.reports.length + attachments.bugNotesAnnotations.length + attachments.remarksAnnotations.length + attachments.documents.length + attachments.maps.length,
            bugsTotal: `${bugsTotal.toFixed(2)}`,
            bugsLength: attachments.bugAnnotations.length,
            reportsTotal: `${reportsTotal.toFixed(2)}`,
            reportsLength: attachments.reports.length,
            bugsNoteTotal: `${bugsNoteTotal.toFixed(2)}`,
            bugsNoteLength: attachments.bugNotesAnnotations.length,
            remarksTotal: `${remarksTotal.toFixed(2)}`,
            remarksLength: attachments.remarksAnnotations.length,
            documentsTotal: `${documentsTotal.toFixed(2)}`,
            documentsLength: attachments.documents.length,
            mapsTotal: `${mapsTotal.toFixed(2)}`,
            mapsLength: attachments.maps.length,
            failedAttachments,
        });
        return { failedAttachments };
    }

    static convertBytesToGo(bytes: number): number {
        return bytes / (1024 * 1024 * 1024);
    }

    /**
     * Repairs the open project, step by step, and logs what each step found and did. Used by the support command
     * `auto-fix-project` (its logs go in the report sent to the support).
     *
     * ```
     * > await Archipad.Commands.autoFixProject()
     * ```
     *
     * @returns What each step fixed, and what it found but cannot fix (to fix by hand)
     */
    static async autoFixProject(): Promise<AutoFixProjectResult> {
        const result: AutoFixProjectResult = { fixed: {}, remaining: {} };
        const step = async (name: string, run: () => Promise<{ fixed: number; remaining: number }>) => {
            console.log(`▶ ${name}`);
            const { fixed, remaining } = await run();
            result.fixed[name] = fixed;
            result.remaining[name] = remaining;
        };
        console.log('Starting the auto-fix of the project...');

        // first, as it deletes the entities whose created attachment is missing: the next steps do not see them
        await step('invalid temp attachments', () => Commands.removeInvalidTmpAttachments());
        await step('bugs without state note', async () => ({ fixed: await Commands.fixBugNotes(), remaining: 0 }));
        await step('duplicated templateIds', async () => ({ fixed: await Commands.fixDuplicatedTemplateIds(), remaining: 0 }));
        await step('duplicated map names', () => Commands.fixDuplicatedMapNames());
        await step('map attachments', () => Commands.fixAllMapAttachments());
        // after the fixes, so that it only reports what is left
        await step('entity integrity', async () => ({ fixed: 0, remaining: (await Commands.checkAllEntities()).issueCount }));
        await step('attachments', async () => {
            if (OfflineServiceInstance.isOffline()) {
                console.warn('⚠️ Offline: the attachments cannot be checked');
                return { fixed: 0, remaining: 0 };
            }
            return { fixed: 0, remaining: (await Commands.checkAllAttachments()).failedAttachments };
        });

        const fixed = Object.values(result.fixed).reduce((total, count) => total + count, 0);
        const remaining = Object.values(result.remaining).reduce((total, count) => total + count, 0);
        console.log(`${fixed > 0 ? '✅' : 'ℹ️'} Auto-fix done: ${fixed} change(s) made, ${remaining} issue(s) left to fix by hand`, result);
        return result;
    }

    /**
     * Clears the templateId of the entities that share it with another one (the first keeps it).
     *
     * @returns The number of entities changed
     */
    static async fixDuplicatedTemplateIds(): Promise<number> {
        const project = await Commands.getProject();
        const entitiesByTemplateId = new Map<string, BaseEntity[]>();
        for (const entities of Object.values(project.getContext().entitiesByType)) {
            for (const entity of Object.values(entities)) {
                if ('templateId' in entity && entity.templateId && typeof entity.templateId === 'string') {
                    const templateId = entity.templateId.toLocaleLowerCase();
                    entitiesByTemplateId.set(templateId, [...(entitiesByTemplateId.get(templateId) ?? []), entity]);
                }
            }
        }
        let fixed = 0;
        for (const [templateId, entities] of entitiesByTemplateId) {
            if (entities.length < 2) {
                continue;
            }
            const duplicates = entities.slice(1);
            console.error(`❌ templateId ${templateId} is shared by ${entities.length} entities: kept on ${entities[0].entityType.entityName}<${entities[0].id}>, cleared on ${duplicates.map((entity) => `${entity.entityType.entityName}<${entity.id}>`).join(', ')}`);
            for (const entity of duplicates) {
                (entity as BaseEntity & { templateId: string | null }).templateId = null;
                fixed++;
            }
        }
        if (fixed === 0) {
            console.log('✅ No duplicated templateIds found.');
        }
        return fixed;
    }

    /**
     * Deletes the maps without observations that have the name of another map, when at most one of these maps has
     * observations: that one is kept (or the first one, when none has observations).
     *
     * @returns The number of maps deleted, and the names shared by several maps with observations (left as they are)
     */
    static async fixDuplicatedMapNames(): Promise<{ fixed: number; remaining: number }> {
        const project = await Commands.getProject();
        const mapsByName = new Map<string, MapEntity[]>();
        for (const map of project.maps) {
            if (map.filename) {
                mapsByName.set(map.filename, [...(mapsByName.get(map.filename) ?? []), map]);
            }
        }
        let fixed = 0;
        let remaining = 0;
        for (const [mapName, maps] of mapsByName) {
            if (maps.length < 2) {
                continue;
            }
            console.error(`❌ ${maps.length} maps are named ${mapName}: ${maps.map((map) => `Map<${map.id}> (${map.bugs.length} observations)`).join(', ')}`);
            const mapsWithBugs = maps.filter((map) => map.bugs.length > 0);
            if (mapsWithBugs.length > 1) {
                console.error(`❌ ${mapsWithBugs.length} of them have observations: none is deleted, to fix by hand`);
                remaining++;
                continue;
            }
            const kept = mapsWithBugs[0] ?? maps[0];
            for (const map of maps.filter((map) => map !== kept)) {
                project.getContext().deleteEntity(map);
                console.log(`✅ Deleted Map<${map.id}> (no observations), keeping Map<${kept.id}>`);
                fixed++;
            }
        }
        if (fixed === 0 && remaining === 0) {
            console.log('✅ No duplicated map names found.');
        }
        return { fixed, remaining };
    }

    /**
     * Fixes the PDF maps whose file is not under their filename key (see {@link Commands.fixMapAttachments}).
     *
     * @returns The number of maps fixed, and of maps that could not be fixed
     */
    static async fixAllMapAttachments(): Promise<{ fixed: number; remaining: number }> {
        const project = await Commands.getProject();
        let fixed = 0;
        let remaining = 0;
        for (const map of project.maps) {
            if (map.fileType !== MAP_FILE_TYPES.PDF || map.type !== MAP_TYPES.DEFAULT || map.filename in map.getAttachments()) {
                continue;
            }
            if (await Commands.fixMapAttachments(map.id)) {
                fixed++;
            } else {
                remaining++;
            }
        }
        if (fixed === 0 && remaining === 0) {
            console.log('✅ Every PDF map has its file.');
        }
        return { fixed, remaining };
    }

    /**
     * Puts the file of the active version of a PDF map under the filename key of the map, when it is missing there.
     *
     * @returns Whether the map was fixed
     */
    static async fixMapAttachments(mapId: string): Promise<boolean> {
        const projectEntity = await Commands.getProject();
        const map = projectEntity.getContext().getEntity('Map', mapId);
        if (!map) {
            console.error(`❌ Unable to find the Map<${mapId}>`);
            return false;
        }
        if (map.fileType !== MAP_FILE_TYPES.PDF || map.type !== MAP_TYPES.DEFAULT) {
            console.error(`❌ Map<${mapId}> is not of type PDF/DEFAULT, skipping attachment fix`);
            return false;
        }
        const attachments = map.getAttachments();
        const keys = Object.keys(attachments);
        if (keys.includes(map.filename)){
            console.log(`✅ Map ${map.id} already has a valid attachment, no need to fix anything`);
            return false;
        }
        console.error(`❌ Map ${map.id} has an invalid file attachment, expected key: ${map.filename}, actual keys: ${keys.join(', ')}, fixing it now...`);
        const mapVersion = map.activeVersion;
        if (!mapVersion) {
            console.error(`❌ Map ${map.id} does not have an active version, cannot fix attachment`);
            return false;
        }
        const attachment = Object.values(mapVersion.getAttachments())[0];
        if (!attachment) {
            console.error(`❌ Map ${map.id} does not have any attachment on its active version, cannot fix attachment`);
            return false;
        }
        map.setAttachmentForKey(map.filename, attachment);
        console.log(`✅ Map ${map.id} attachment has been fixed, new keys: ${Object.keys(map.getAttachments()).join(', ')}`);
        return true;
    }

    /**
     * The observations of the open project whose sign-off (quitus) is signed, each with the state a cancellation of the
     * sign-off puts it back in (null when it has no state before the sign-off).
     */
    static async listSignedOffBugs(): Promise<{ bug: BugEntity; previousState: BugStateEntity | null }[]> {
        const project = await Commands.getProject();
        return project.bugs
            .filter((bug) => !bug.relatedParentBug && Commands.signOffService.isApprovedSignOff(bug))
            .sort((a, b) => a.index - b.index)
            .map((bug) => ({ bug, previousState: Commands.signOffService.findStateBeforeApprovedSignOff(bug) }));
    }

    /**
     * Cancels the signed sign-off (quitus) of observations of the open project: a new note puts each one back in the
     * state it was in before the sign-off. The changes are published by the next synchronization. Used by the support
     * from the debug menu (`ui-cancel-sign-off-dialog`).
     *
     * ```
     * > await Archipad.Commands.cancelSignOff(12, 15)
     * ```
     *
     * @param bugIndexes The numbers of the observations
     * @returns The notes created
     */
    static async cancelSignOff(...bugIndexes: number[]): Promise<BugNoteEntity[]> {
        const signedOff = await Commands.listSignedOffBugs();
        const notes: BugNoteEntity[] = [];
        for (const index of bugIndexes) {
            const signedOffBug = signedOff.find(({ bug }) => bug.index === index);
            if (!signedOffBug) {
                console.error(`❌ Observation ${index}: no signed sign-off`);
                continue;
            }
            if (!signedOffBug.previousState) {
                console.error(`❌ Observation ${index}: no state before the sign-off, to fix by hand`);
                continue;
            }
            notes.push(Commands.signOffService.cancelApprovedSignOff(signedOffBug.bug));
            console.log(`✅ Observation ${index}: sign-off cancelled, back to ${signedOffBug.previousState.name}`);
        }
        return notes;
    }

    private static get signOffService(): SignOffService {
        return new SignOffService(WorkflowManager, new BugService(), BugNoteFacadeInstance);
    }

    /**
     * Creates the missing opening note of the bugs without state note, and sets the state of their opening and closing
     * notes.
     *
     * @returns The number of bugs fixed
     */
    static async fixBugNotes(): Promise<number> {
        const projectEntity = await Commands.getProject();
        const workflowService = new WorkflowService();
        const bugNotesWithoutState = projectEntity.bugs.filter(bug => !bug.stateNoteCache);
        if (bugNotesWithoutState.length === 0) {
            console.log('✅ All bug notes have a state, no need to fix anything');
            return 0;
        } else {
            console.error(`❌ There are ${bugNotesWithoutState.length} bugs without state note, fixing them now...`);
        }
        for (const bug of bugNotesWithoutState) {

            const bugNoteOpenId = generateDeterministicId(bug.bugId, "1AB0DC21BB2D4EC28B5DFDC82898AC89", "uuid");
            const bugNoteCloseId = generateDeterministicId(bug.bugId, "887329BD114949CCB2D0CBCC31B3F344", "uuid");

            let bugNoteOpen = bug.getContext().getEntity('BugNote', bugNoteOpenId);
            let bugNoteClose = bug.getContext().getEntity('BugNote', bugNoteCloseId);

            if (!bugNoteOpen) {
                bugNoteOpen = BugNoteFacadeInstance.createBugNoteModelPrimitive(bug, bugNoteOpenId);
                bugNoteOpen.creationDate = bug.openDate;
                bugNoteOpen.text = null;
                bugNoteOpen.visit = bug.visit;
                bugNoteOpen.date = bug.openDate || bug.visit?.date;
                bugNoteOpen.user = null;
            }

            if (bugNoteOpen) {
                bugNoteOpen.bugState = workflowService.findEquivBugState(EquivBugState.EquivStateOpen, projectEntity);
                bugNoteOpen.bugTransition = workflowService.findEquivTransition(EquivBugTransition.EquivTransitionEnter, bugNoteOpen.bugState, projectEntity)
            }

            if (bugNoteClose) {
                bugNoteClose.bugState = workflowService.findEquivBugState(EquivBugState.EquivStateClosed, projectEntity);
                bugNoteClose.bugTransition = workflowService.findEquivTransition(EquivBugTransition.EquivTransitionAccept, bugNoteClose.bugState, projectEntity)
            }
        }
        console.log(`✅ ${bugNotesWithoutState.length} bugs fixed`);
        return bugNotesWithoutState.length;
    }

    /**
     * Undoes the attachment changes of the local changes (temp patch) whose file is missing on the server, which would
     * keep the changes from being published: an entity created with such an attachment is deleted, an attachment
     * changed to such a file gets back the published one (the change is dropped from the local changes).
     *
     * Only a file the server reports missing (404) is undone: offline, or on any other error, nothing is changed. The
     * attachments waiting in the local cache (taken offline, uploaded later) are not checked.
     *
     * @returns The number of attachment changes undone, and of attachments that could not be checked
     */
    static async removeInvalidTmpAttachments(): Promise<{ fixed: number; remaining: number }> {
        const entityContext = await Commands.getEntityContext();
        const changes = [
            ...Commands.listAttachmentChanges('created', entityContext.createdAttachments),
            ...Commands.listAttachmentChanges('updated', entityContext.updatedAttachments),
        ];
        if (changes.length === 0) {
            console.log('✅ No attachment in the local changes.');
            return { fixed: 0, remaining: 0 };
        }
        if (OfflineServiceInstance.isOffline()) {
            console.warn(`⚠️ Offline: the ${changes.length} attachment(s) of the local changes cannot be checked`);
            return { fixed: 0, remaining: changes.length };
        }

        // checked first, changed after: changing the entities changes the lists being read
        const missing: typeof changes = [];
        let unchecked = 0;
        for (const change of changes) {
            const entity = entityContext.getEntity(change.entityName, change.entityId);
            const attachment = entity?.getAttachmentForKey(change.key);
            if (!entity || !attachment) {
                continue;
            }
            if (attachment.type === 'localCache') {
                continue;
            }
            try {
                await firstValueFrom(FileService.metadata(attachment));
            } catch (error) {
                if (Commands.isMissingFileError(error)) {
                    console.error(`❌ The file of ${change.entityName}<${change.entityId}> "${change.key}" (${change.operation}) is missing on the server`);
                    missing.push(change);
                } else {
                    console.warn(`⚠️ Unable to check the file of ${change.entityName}<${change.entityId}> "${change.key}", left as it is`, error);
                    unchecked++;
                }
            }
        }

        const deletedEntities = new Set<string>();
        for (const { operation, entityName, entityId, key } of missing) {
            const entity = entityContext.getEntity(entityName, entityId);
            if (!entity || deletedEntities.has(`${entityName}<${entityId}>`)) {
                continue;
            }
            if (operation === 'created') {
                entityContext.deleteEntity(entity);
                deletedEntities.add(`${entityName}<${entityId}>`);
                console.log(`✅ Deleted ${entityName}<${entityId}>, created with the missing file "${key}"`);
            } else {
                // drops the change of this key from the local changes: the published attachment comes back on reload
                entity.setAttachmentForKey(key, null);
                delete entityContext['_updatedAttachments'][entityName]?.[entityId]?.[key];
                console.log(`✅ Undid the change of "${key}" on ${entityName}<${entityId}>: its published file comes back on reload`);
            }
        }
        if (missing.length === 0 && unchecked === 0) {
            console.log(`✅ The ${changes.length} attachment(s) of the local changes are on the server.`);
        }
        return { fixed: missing.length, remaining: unchecked };
    }

    /**
     * @internal
     * The attachment changes of an entity context (`createdAttachments` or `updatedAttachments`), as a list.
     */
    private static listAttachmentChanges(
        operation: 'created' | 'updated',
        changes: { [entityName: string]: { [entityId: string]: { [key: string]: unknown } } } | null,
    ): Array<{ operation: 'created' | 'updated'; entityName: string; entityId: string; key: string }> {
        return Object.entries(changes ?? {}).flatMap(([entityName, entities]) =>
            Object.entries(entities).flatMap(([entityId, keys]) =>
                Object.keys(keys).map((key) => ({ operation, entityName, entityId, key })),
            ),
        );
    }

    /**
     * @internal
     * Whether a file error means the file does not exist: S3 answers 404 to a HEAD request (`NotFound`, which
     * `wrapS3Error` does not turn into a {@link FileNotFoundError}).
     */
    private static isMissingFileError(error: unknown): boolean {
        if (error instanceof FileNotFoundError) {
            return true;
        }
        const underlying = (error as { underlyingError?: { statusCode?: number; code?: string } } | null)?.underlyingError;
        return underlying?.statusCode === 404 || underlying?.code === 'NotFound' || underlying?.code === 'NoSuchKey';
    }

    /**
     * Creates observations at random positions on the plan currently displayed, each one with a
     * random photo. Uses the same paths as the UI: the photo is uploaded to the temp patch folder
     * when online, or stored in the local cache (uploaded later) when offline.
     *
     * Usage: open a project on the observations screen of a plan, then:
     * ```
     * > await Archipad.Commands.generateObservations()
     * > await Archipad.Commands.generateObservations(5)
     * ```
     *
     * @param count - Number of observations to create, defaults to 20
     */
    static async generateObservations(count = 20): Promise<string> {
        const bugMapController = Commands.deepQuerySelector('bug-map-controller') as any;
        const bugController = bugMapController?.getRootNode()?.host as any;
        const mapModel = bugMapController?.model as MapEntity | undefined;
        const viewBox = (Commands.deepQuerySelector('ui-map-view') as any)?._mapViewState?.renderer?.pdfViewport?.viewBox as number[] | undefined;
        if (!bugController?.projectModel || !mapModel || !viewBox) {
            throw new IllegalStateError('Open the observations screen of a plan first (the plan must be displayed)');
        }

        const project = bugController.projectModel as ProjectEntity;
        const currentAccount = await _LegacyServiceManager.container.get(Account_Service_DIID.symbol).fetchCurrentAccount();
        const bugFacade = new BugFacade(getArchipadAbilityService(), new BugService(), WorkflowManager, new PhaseService());
        const annotationService = new AnnotationService();
        const isOffline = OfflineServiceInstance.isOffline();
        const [minX, minY, maxX, maxY] = viewBox;

        for (let i = 1; i <= count; i++) {
            const bug = bugFacade.openNewBug({
                project,
                map: mapModel,
                visit: bugController.visitModel ?? null,
                bugGroup: bugController.bugGroupModel ?? null,
                temp: true,
                index: 0,
                location: null,
            });
            bug.positionX = minX + Math.random() * (maxX - minX);
            bug.positionY = minY + Math.random() * (maxY - minY);
            bug.text = `PLUS-2107 generated observation ${i}/${count}`;
            makeTempBugConcrete(bug);

            const annotation = annotationService.makeAnnotationForAnnotated(bug);
            annotation.hasMetadata = true;
            annotation.drawingHint = DrawingHint.DRAWING_NO;
            const photo = await Commands.makeRandomJpeg(`#${bug.index}`);
            const attachmentKey = 'annotation.jpg';

            if (isOffline) {
                // same as AnnotatedEntityDelegate.pickAsyncNativeAttachment()
                const userId = currentAccount.id.toString();
                await _LegacyServiceManager.container.get(AsyncUploadAttachmentService_DIID.symbol).postUploadAttachmentTask({
                    attachmentKey,
                    entityId: annotation.id,
                    entityType: annotation.entityType.entityName,
                    attachmentData: photo,
                    userId,
                    projectId: project.id,
                });
                const localCachePatch = new PatchDescriptor(new PatchesDescriptor(project.id, "localCache"), "current");
                annotation.setAttachmentForKey(attachmentKey, new CacheFileDescriptor(localCachePatch, annotation.id, attachmentKey, project.id, userId));
            } else {
                // same as AnnotatedEntityDelegate.setAttachment()
                const tempPatch = new PatchDescriptor(new PatchesDescriptor(project.id, "temp"), "current");
                const attachment = await firstValueFrom(prepareAttachment(voidProgress(), tempPatch, Data.fromBlob(photo), annotation.id, attachmentKey));
                annotation.setAttachmentForKey(attachmentKey, attachment);
            }

            console.log(`Observation ${i}/${count} created (#${bug.index})`);
        }

        return `${count} observations created on the plan "${mapModel.nameComputed}" (${isOffline ? 'photos in local cache' : 'photos uploaded'})`;
    }

    /**
     * @internal
     * Generates a random JPEG image (random colors and shapes) from a base64 data URL.
     */
    private static async makeRandomJpeg(label: string): Promise<Blob> {
        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 600;
        const ctx = canvas.getContext('2d')!;
        const randomColor = () => `hsl(${Math.floor(Math.random() * 360)}, 70%, ${30 + Math.floor(Math.random() * 40)}%)`;

        ctx.fillStyle = randomColor();
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        for (let i = 0; i < 15; i++) {
            ctx.fillStyle = randomColor();
            ctx.beginPath();
            ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, 20 + Math.random() * 120, 0, 2 * Math.PI);
            ctx.fill();
        }
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 96px sans-serif';
        ctx.fillText(label, 40, 120);

        const base64 = canvas.toDataURL('image/jpeg', 0.8);
        return (await fetch(base64)).blob();
    }

    /**
     * @internal
     * `querySelector` that also looks inside shadow roots.
     */
    private static deepQuerySelector(selector: string, root: Document | ShadowRoot | Element = document): Element | null {
        const found = root.querySelector(selector);
        if (found) {
            return found;
        }
        for (const element of Array.from(root.querySelectorAll('*'))) {
            if (element.shadowRoot) {
                const foundInShadow = Commands.deepQuerySelector(selector, element.shadowRoot);
                if (foundInShadow) {
                    return foundInShadow;
                }
            }
        }
        return null;
    }
}

/**
 * This global injection is intentionally opaque because those
 * commands should be used from the browser console only.
 */
(window as any).Archipad = {...(window as any).Archipad, Commands};
